/**
 * TemplateEditor Component
 *
 * Editor for document templates with live preview.
 * Supports Jinja2 template syntax with context variables.
 */

import { useState, useEffect, useCallback } from 'react';
import { FileCode, Eye, Save, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { Button, Callout, ChipGroup, Textarea } from '@elbklar/ui';
import { useUpdateTemplates, useTemplatePreview } from '@/hooks/useTenantConfig';

interface TenantConfig {
  quote_header: string | null;
  quote_footer: string | null;
  invoice_header: string | null;
  invoice_footer: string | null;
  email_signature: string | null;
}

interface TemplateEditorProps {
  config: TenantConfig;
}

type TemplateType = 'quote_header' | 'quote_footer' | 'invoice_header' | 'invoice_footer' | 'email_signature';

const TEMPLATE_TYPES: { id: TemplateType; label: string; description: string }[] = [
  { id: 'quote_header', label: 'Angebot Kopfzeile', description: 'Erscheint oben auf Angebotsdokumenten' },
  { id: 'quote_footer', label: 'Angebot Fußzeile', description: 'Erscheint unten auf Angebotsdokumenten' },
  { id: 'invoice_header', label: 'Rechnung Kopfzeile', description: 'Erscheint oben auf Rechnungsdokumenten' },
  { id: 'invoice_footer', label: 'Rechnung Fußzeile', description: 'Erscheint unten auf Rechnungsdokumenten' },
  { id: 'email_signature', label: 'E-Mail-Signatur', description: 'Wird an ausgehende E-Mails angehängt' },
];

// Namen wie im Render-Kontext von TemplateService.get_context() (elbklar-core):
// die Firmendaten haengen unter `company`, nicht flach im Wurzel-Namensraum.
const AVAILABLE_VARIABLES = [
  { name: 'company.name', description: 'Firmenname' },
  { name: 'company.legal_name', description: 'Vollständiger Firmenname' },
  { name: 'company.email', description: 'E-Mail-Adresse' },
  { name: 'company.phone', description: 'Telefonnummer' },
  { name: 'company.website', description: 'Website-URL' },
  { name: 'company.street', description: 'Straße' },
  { name: 'company.postal_code', description: 'Postleitzahl' },
  { name: 'company.city', description: 'Ort' },
  { name: 'company.country', description: 'Land' },
  { name: 'company.full_address', description: 'Vollständige Adresse' },
  { name: 'company.vat_id', description: 'USt-IdNr.' },
  { name: 'company.tax_number', description: 'Steuernummer' },
  { name: 'company.registry_court', description: 'Amtsgericht' },
  { name: 'company.registry_number', description: 'Handelsregister-Nr.' },
  { name: 'company.managing_directors', description: 'Geschäftsführung' },
  { name: 'customer.name', description: 'Kundenname' },
  { name: 'customer.contact_name', description: 'Ansprechpartner' },
  { name: 'quote.number', description: 'Angebotsnummer' },
  { name: 'quote.total', description: 'Angebot: Gesamtbetrag' },
  { name: 'invoice.number', description: 'Rechnungsnummer' },
  { name: 'invoice.due_date', description: 'Rechnung: Fälligkeit' },
  { name: 'user.name', description: 'Benutzername (Signatur)' },
  { name: 'today', description: 'Heutiges Datum' },
];

export function TemplateEditor({ config }: TemplateEditorProps) {
  const [selectedType, setSelectedType] = useState<TemplateType>('quote_header');
  const [templates, setTemplates] = useState<Record<TemplateType, string>>({
    quote_header: config.quote_header || '',
    quote_footer: config.quote_footer || '',
    invoice_header: config.invoice_header || '',
    invoice_footer: config.invoice_footer || '',
    email_signature: config.email_signature || '',
  });
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState<string>('');
  const [previewError, setPreviewError] = useState<string | null>(null);

  const updateMutation = useUpdateTemplates();
  const previewMutation = useTemplatePreview();

  const currentTemplate = templates[selectedType];

  // Debounced preview
  const updatePreview = useCallback(async (template: string) => {
    if (!template.trim()) {
      setPreviewContent('');
      setPreviewError(null);
      return;
    }

    try {
      const result = await previewMutation.mutateAsync(template);
      if (result.valid) {
        setPreviewContent(result.rendered);
        setPreviewError(null);
      } else {
        setPreviewContent('');
        setPreviewError(result.error || 'Ungültige Vorlage');
      }
    } catch {
      setPreviewError('Vorschau ließ sich nicht erzeugen');
    }
  }, [previewMutation]);

  // Update preview when template changes (debounced)
  useEffect(() => {
    if (!showPreview) return;

    const timer = setTimeout(() => {
      updatePreview(currentTemplate);
    }, 500);

    return () => clearTimeout(timer);
  }, [currentTemplate, showPreview, updatePreview]);

  const handleTemplateChange = (value: string) => {
    setTemplates((prev) => ({
      ...prev,
      [selectedType]: value,
    }));
  };

  const handleSave = async () => {
    await updateMutation.mutateAsync({
      quote_header: templates.quote_header || undefined,
      quote_footer: templates.quote_footer || undefined,
      invoice_header: templates.invoice_header || undefined,
      invoice_footer: templates.invoice_footer || undefined,
      email_signature: templates.email_signature || undefined,
    });
  };

  const insertVariable = (varName: string) => {
    const insertion = `{{ ${varName} }}`;
    handleTemplateChange(currentTemplate + insertion);
  };

  const hasChanges =
    templates.quote_header !== (config.quote_header || '') ||
    templates.quote_footer !== (config.quote_footer || '') ||
    templates.invoice_header !== (config.invoice_header || '') ||
    templates.invoice_footer !== (config.invoice_footer || '') ||
    templates.email_signature !== (config.email_signature || '');

  return (
    <div className="space-y-6">
      {/* Template Type Selection */}
      <div className="flex flex-col gap-2">
        <span className="t-body-strong">Vorlage wählen</span>
        <ChipGroup
          label="Vorlage wählen"
          value={selectedType}
          onChange={(v) => setSelectedType(v as TemplateType)}
          items={TEMPLATE_TYPES.map((type) => ({ value: type.id, label: type.label }))}
        />
        <p className="t-small text-muted-foreground">{TEMPLATE_TYPES.find((t) => t.id === selectedType)?.description}</p>
      </div>

      {/* Editor Area */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Code Editor */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="flex items-center gap-2 t-body-strong">
              <FileCode size={16} strokeWidth={1.75} aria-hidden />
              Template-Code
            </span>
            <Button size="sm" variant="ghost" icon={Eye} aria-pressed={showPreview} onClick={() => setShowPreview(!showPreview)} className={showPreview ? 'bg-highlight-soft text-highlight hover:bg-highlight-soft' : undefined}>
              Vorschau
            </Button>
          </div>
          <Textarea
            aria-label="Code der Vorlage"
            value={currentTemplate}
            onChange={(e) => handleTemplateChange(e.target.value)}
            rows={12}
            className="resize-y font-mono text-[13px]"
            placeholder="Vorlage in Jinja2-Syntax"
          />
        </div>

        {/* Preview / Variables Panel */}
        <div>
          {showPreview ? (
            <div>
              <span className="mb-2 flex items-center gap-2 t-body-strong">
                <Eye size={16} strokeWidth={1.75} aria-hidden />
                Live-Vorschau
              </span>
              <div className="min-h-[280px] rounded-lg border border-border bg-muted p-4">
                {previewMutation.isPending && <div className="t-small text-muted-foreground">Lädt</div>}
                {previewError && (
                  <div className="flex items-start gap-2 text-danger">
                    <AlertCircle size={16} strokeWidth={1.75} className="mt-0.5 shrink-0" aria-hidden />
                    <span className="t-small">{previewError}</span>
                  </div>
                )}
                {!previewMutation.isPending && !previewError && (
                  <div
                    className="t-body"
                    dangerouslySetInnerHTML={{ __html: previewContent || '<span class="text-muted-foreground">Leere Vorlage</span>' }}
                  />
                )}
              </div>
            </div>
          ) : (
            <div>
              <span className="mb-2 flex items-center gap-2 t-body-strong">
                <Info size={16} strokeWidth={1.75} aria-hidden />
                Verfuegbare Variablen
              </span>
              <div className="max-h-[280px] overflow-y-auto rounded-lg border border-border bg-muted p-2">
                <p className="mb-2 px-2 t-small text-muted-foreground">Variable antippen, um sie einzufügen:</p>
                <div className="space-y-0.5">
                  {AVAILABLE_VARIABLES.map((variable) => (
                    <button
                      key={variable.name}
                      onClick={() => insertVariable(variable.name)}
                      className="group flex w-full cursor-pointer items-center justify-between rounded-sm px-2 py-1.5 text-left transition-colors hover:bg-accent"
                    >
                      <code className="t-mono text-highlight">{'{{ ' + variable.name + ' }}'}</code>
                      <span className="t-small text-muted-foreground group-hover:text-foreground">{variable.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Syntax Help */}
      <Callout tone="info" icon={Info} title="Jinja2-Syntax" boxed>
        <div className="mt-1 grid grid-cols-1 gap-3 t-small md:grid-cols-2">
          <div>
            <p className="font-medium">Variablen:</p>
            <code className="t-mono">{'{{ variable_name }}'}</code>
          </div>
          <div>
            <p className="font-medium">Bedingte Anzeige:</p>
            <code className="t-mono">{'{% if variable %}...{% endif %}'}</code>
          </div>
          <div>
            <p className="font-medium">Filter (etwa Default):</p>
            <code className="t-mono">{'{{ variable | default("Fallback") }}'}</code>
          </div>
          <div>
            <p className="font-medium">HTML-Escape:</p>
            <code className="t-mono">{'{{ variable | e }}'}</code>
          </div>
        </div>
      </Callout>

      {/* Save Button */}
      <div className="flex flex-wrap items-center gap-4 border-t border-border pt-4">
        <Button variant="primary" icon={Save} onClick={handleSave} loading={updateMutation.isPending} disabled={!hasChanges}>
          Alle Vorlagen speichern
        </Button>
        {hasChanges && <span className="t-small text-warning">Ungespeicherte Änderungen</span>}
        {updateMutation.isSuccess && !hasChanges && (
          <span className="flex items-center gap-1 t-small text-success">
            <CheckCircle size={16} strokeWidth={1.75} aria-hidden />
            Gespeichert
          </span>
        )}
        {updateMutation.isError && (
          <span className="t-small text-danger">{(updateMutation.error as Error)?.message || 'Speichern fehlgeschlagen'}</span>
        )}
      </div>
    </div>
  );
}

export default TemplateEditor;
